import React, { useState, useMemo } from 'react';
import { getTrades } from '../../services/storage';
import { buildStats, buildBreakdowns, buildEquityCurve, buildPsychStats } from '../../utils/statsCalculator';

const THEME = {
  colors: {
    bg:        '#0a0e1a',
    card:      '#111827',
    border:    '#1e293b',
    text:      '#e2e8f0',
    textMuted: '#94a3b8',
    green:     '#00ff88',
    red:       '#ff3333',
    gold:      '#ffd700',
    blue:      '#00d4ff',
    purple:    '#a78bfa',
  },
};

const S = {
  page:    { padding: '16px', color: THEME.colors.text, fontFamily: 'monospace', maxWidth: '100%' },
  title:   { fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, marginBottom: '4px', letterSpacing: '2px' },
  sub:     { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '16px', letterSpacing: '1px' },
  card:    (bg) => ({ background: bg || THEME.colors.card, border: `1px solid ${THEME.colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }),
  section: { fontSize: '10px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '2px', marginBottom: '10px' },
  btn:     (active) => ({ background: active ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${active ? THEME.colors.blue : THEME.colors.border}`, borderRadius: '6px', padding: '7px 10px', color: active ? THEME.colors.blue : THEME.colors.textMuted, fontSize: '11px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: active ? '700' : '400' }),
};

const resultColor = (r) => r === 'WIN' ? THEME.colors.green : r === 'LOSS' ? THEME.colors.red : THEME.colors.textMuted;
const pnlColor    = (v) => v > 0 ? THEME.colors.green : v < 0 ? THEME.colors.red : THEME.colors.textMuted;

// ─── Big Stat Card ───────────────────────────────────────────────────────────
function BigStat({ label, value, color, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginBottom: '2px', letterSpacing: '1px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: '700', color: color || THEME.colors.gold }}>{value}</div>
      {sub && <div style={{ fontSize: '10px', color: THEME.colors.textMuted }}>{sub}</div>}
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────────────────────────
function OverviewTab({ stats, trades }) {
  if (!stats) {
    return (
      <div style={{ ...S.card(), textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>📊</div>
        <div style={{ color: THEME.colors.textMuted, fontSize: '13px' }}>No closed trades yet.</div>
        <div style={{ color: THEME.colors.textMuted, fontSize: '12px', marginTop: '6px' }}>Close your first trade in the Journal to see stats.</div>
      </div>
    );
  }

  const streakColor = stats.streak.type === 'WIN' ? THEME.colors.green : stats.streak.type === 'LOSS' ? THEME.colors.red : THEME.colors.textMuted;

  return (
    <>
      {/* Main KPIs */}
      <div style={{ ...S.card('rgba(255,215,0,0.06)'), padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <BigStat label="WIN RATE"    value={`${stats.winRate}%`}  color={stats.winRate >= 60 ? THEME.colors.green : stats.winRate >= 45 ? THEME.colors.gold : THEME.colors.red} sub={`${stats.wins}W / ${stats.losses}L / ${stats.bes}BE`} />
          <BigStat label="TOTAL P&L"  value={`${stats.totalPnl >= 0 ? '+' : ''}$${stats.totalPnl}`} color={pnlColor(stats.totalPnl)} sub={`${stats.total} trades closed`} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          <BigStat label="AVG R:R"  value={`1:${stats.avgRR}`}  color={stats.avgRR >= 2 ? THEME.colors.green : THEME.colors.gold} />
          <BigStat label="STREAK"   value={`${stats.streak.count}×`} color={streakColor} sub={stats.streak.type} />
          <BigStat label="TRADES"   value={trades.filter(t => t.result === 'OPEN').length} color={THEME.colors.blue} sub="open now" />
        </div>
      </div>

      {/* Best / Worst */}
      {(stats.bestTrade || stats.worstTrade) && (
        <div style={S.card()}>
          <div style={S.section}>BEST & WORST TRADES</div>
          {stats.bestTrade && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px' }}>
              <span style={{ color: THEME.colors.textMuted }}>🏆 Best</span>
              <span>{stats.bestTrade.id} — {stats.bestTrade.date}</span>
              <span style={{ color: THEME.colors.green, fontWeight: '700' }}>+${stats.bestTrade.pnl_usd}</span>
            </div>
          )}
          {stats.worstTrade && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
              <span style={{ color: THEME.colors.textMuted }}>📉 Worst</span>
              <span>{stats.worstTrade.id} — {stats.worstTrade.date}</span>
              <span style={{ color: THEME.colors.red, fontWeight: '700' }}>${stats.worstTrade.pnl_usd}</span>
            </div>
          )}
        </div>
      )}

      {/* Grade quality */}
      <div style={S.card()}>
        <div style={S.section}>GRADE DISCIPLINE</div>
        {['A+', 'A', 'B', 'C', 'D'].map(g => {
          const grp = trades.filter(t => t.setup_grade === g && t.result !== 'OPEN');
          if (!grp.length) return null;
          const wins = grp.filter(t => t.result === 'WIN').length;
          const wr   = Math.round((wins / grp.length) * 100);
          return (
            <div key={g} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ width: '24px', fontSize: '12px', fontWeight: '700', color: g === 'A+' ? THEME.colors.green : g === 'A' ? THEME.colors.blue : g === 'B' ? THEME.colors.gold : THEME.colors.red }}>{g}</span>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: '3px', height: '6px', overflow: 'hidden' }}>
                <div style={{ width: `${wr}%`, height: '100%', background: wr >= 60 ? THEME.colors.green : wr >= 45 ? THEME.colors.gold : THEME.colors.red, borderRadius: '3px' }} />
              </div>
              <span style={{ fontSize: '11px', color: THEME.colors.textMuted, width: '60px', textAlign: 'right' }}>{wr}% ({grp.length})</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Breakdown Tab ───────────────────────────────────────────────────────────
function BreakdownRow({ label, total, wins, winRate, pnl }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
        <span style={{ fontSize: '12px', color: THEME.colors.text }}>{label}</span>
        <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{wins}W/{total - wins}L · {winRate}% · <span style={{ color: pnlColor(pnl) }}>{pnl >= 0 ? '+' : ''}${pnl}</span></span>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '3px', height: '5px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(winRate, 100)}%`, height: '100%', background: winRate >= 60 ? THEME.colors.green : winRate >= 45 ? THEME.colors.gold : THEME.colors.red, borderRadius: '3px' }} />
      </div>
    </div>
  );
}

function BreakdownTab({ breakdowns }) {
  const [sub, setSub] = useState('session');
  const subs = [
    { id: 'session',   label: '🕐 Session' },
    { id: 'day',       label: '📅 Day' },
    { id: 'direction', label: '📐 Direction' },
    { id: 'concept',   label: '📖 ICT Concept' },
  ];

  const rows = breakdowns?.[sub] || [];

  return (
    <>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {subs.map(s => (
          <button key={s.id} style={S.btn(sub === s.id)} onClick={() => setSub(s.id)}>{s.label}</button>
        ))}
      </div>
      <div style={S.card()}>
        <div style={S.section}>{subs.find(s => s.id === sub)?.label.toUpperCase()} PERFORMANCE</div>
        {rows.length === 0
          ? <div style={{ color: THEME.colors.textMuted, fontSize: '12px' }}>No data for this breakdown yet.</div>
          : rows.map(r => <BreakdownRow key={r.label} {...r} />)
        }
      </div>
    </>
  );
}

// ─── Equity Curve Tab ────────────────────────────────────────────────────────
function EquityTab({ curve }) {
  if (!curve.length) {
    return (
      <div style={{ ...S.card(), textAlign: 'center', padding: '30px', color: THEME.colors.textMuted, fontSize: '13px' }}>
        No closed trades to show equity curve.
      </div>
    );
  }

  const maxAbs = Math.max(...curve.map(p => Math.abs(p.cumulative)), 1);
  const barMax = 120; // px

  return (
    <div style={S.card()}>
      <div style={S.section}>EQUITY CURVE — CUMULATIVE P&L</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '100px', marginBottom: '12px', padding: '4px 0' }}>
        {curve.map((p, i) => {
          const h = Math.max(Math.abs(p.cumulative) / maxAbs * barMax, 4);
          const isPos = p.cumulative >= 0;
          return (
            <div key={i} title={`${p.id}: ${p.date} | P&L: ${p.cumulative >= 0 ? '+' : ''}$${p.cumulative}`}
              style={{ flex: 1, minWidth: '4px', height: `${h}px`, background: isPos ? THEME.colors.green : THEME.colors.red, borderRadius: '2px 2px 0 0', opacity: 0.85, cursor: 'default' }} />
          );
        })}
      </div>

      {/* Recent trades list */}
      <div style={S.section}>RECENT CLOSED TRADES</div>
      {[...curve].reverse().slice(0, 10).map(p => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px', padding: '5px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: '5px' }}>
          <span style={{ color: THEME.colors.textMuted }}>{p.id}</span>
          <span style={{ color: THEME.colors.textMuted }}>{p.date}</span>
          <span style={{ color: resultColor(p.result), fontWeight: '700' }}>{p.result}</span>
          <span style={{ color: pnlColor(p.pnl) }}>{p.pnl >= 0 ? '+' : ''}${p.pnl}</span>
          <span style={{ color: pnlColor(p.cumulative) }}>Σ{p.cumulative >= 0 ? '+' : ''}${p.cumulative}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Psychology Tab ──────────────────────────────────────────────────────────
function PsychTab({ psychStats }) {
  if (!psychStats) {
    return <div style={{ ...S.card(), color: THEME.colors.textMuted, fontSize: '13px', padding: '30px', textAlign: 'center' }}>No data yet.</div>;
  }
  return (
    <div style={S.card()}>
      <div style={S.section}>PSYCHOLOGY → WIN RATE CORRELATION</div>
      <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '12px', lineHeight: '1.5' }}>
        Does your mental state affect your trading? See the truth below.
      </div>
      {psychStats.map(row => (
        <div key={row.label} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px' }}>{row.label}</span>
            <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>{row.total} trades · {row.wins}W · {row.winRate}%</span>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(row.winRate, 100)}%`, height: '100%', background: row.winRate >= 60 ? THEME.colors.green : row.winRate >= 45 ? THEME.colors.gold : THEME.colors.red, borderRadius: '4px', transition: 'width 0.4s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontSize: '10px', color: THEME.colors.textMuted }}>
            <span>0%</span>
            <span style={{ color: pnlColor(row.pnl) }}>P&L: {row.pnl >= 0 ? '+' : ''}${row.pnl}</span>
            <span>100%</span>
          </div>
        </div>
      ))}
      <div style={{ marginTop: '8px', padding: '10px', background: 'rgba(167,139,250,0.08)', borderRadius: '8px', fontSize: '12px', color: THEME.colors.purple }}>
        💡 Insight: Trade only when psychology ≥ 7. Low mental state = lower win rate.
      </div>
    </div>
  );
}

// --- Jarvis Accuracy Tab ------------------------------------------------
// Trades logged by autoLogSignalAsTrade() (scriptAnalysis.js) are tagged
// 'AUTO-DEMO' in `notes` — this filters to JUST those, so this tab answers
// "how accurate is Jarvis's OWN validated-strategy signal" specifically,
// separate from Sir's manually-logged trades (mixing them would blur the
// question this screen exists to answer).
const isAutoSignalTrade = (t) => t.notes?.startsWith('AUTO-DEMO');
const extractTF = (t) => t.notes?.match(/TF:(\w+)/)?.[1] || 'unknown';

function JarvisAccuracyTab({ trades }) {
  const autoTrades = trades.filter(isAutoSignalTrade);
  const closed = autoTrades.filter(t => t.result && t.result !== 'OPEN');
  const open   = autoTrades.filter(t => t.result === 'OPEN');
  const stats  = useMemo(() => buildStats(autoTrades), [autoTrades]);

  if (autoTrades.length === 0) {
    return (
      <div style={{ ...S.card(), textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>🎯</div>
        <div style={{ color: THEME.colors.textMuted, fontSize: '13px' }}>अजून एकही Jarvis auto-signal नाही.</div>
        <div style={{ color: THEME.colors.textMuted, fontSize: '12px', marginTop: '6px' }}>
          Live Monitor वर "Check Now" दाबा किंवा Chat मध्ये "current setup?" विचारा — खरा signal आला की इथे आपोआप दिसेल.
        </div>
      </div>
    );
  }

  // Per-timeframe breakdown
  const byTF = {};
  closed.forEach(t => {
    const tf = extractTF(t);
    if (!byTF[tf]) byTF[tf] = { total: 0, wins: 0 };
    byTF[tf].total++;
    if (t.result === 'WIN') byTF[tf].wins++;
  });

  return (
    <>
      <div style={{ ...S.card('rgba(0,212,255,0.06)'), padding: '16px' }}>
        <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '10px', letterSpacing: '1px' }}>
          🎯 JARVIS चे स्वतःचे signals (auto-logged, zero AI — pure Structure+Fibonacci+ADX math)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          <BigStat label="ACCURACY" value={stats ? `${stats.winRate}%` : '—'} color={stats && stats.winRate >= 50 ? THEME.colors.green : THEME.colors.red} sub={stats ? `${stats.wins}W / ${stats.losses}L` : 'no closed yet'} />
          <BigStat label="CLOSED"   value={closed.length} color={THEME.colors.blue} />
          <BigStat label="OPEN NOW" value={open.length}   color={THEME.colors.gold} sub="tracking live" />
        </div>
      </div>

      {Object.keys(byTF).length > 0 && (
        <div style={S.card()}>
          <div style={S.section}>TIMEFRAME-WISE ACCURACY</div>
          {Object.entries(byTF).sort((a, b) => b[1].total - a[1].total).map(([tf, d]) => {
            const wr = Math.round((d.wins / d.total) * 100);
            return (
              <div key={tf} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ width: '36px', fontSize: '12px', fontWeight: '700', color: THEME.colors.blue }}>{tf}</span>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: '3px', height: '6px', overflow: 'hidden' }}>
                  <div style={{ width: `${wr}%`, height: '100%', background: wr >= 50 ? THEME.colors.green : THEME.colors.red, borderRadius: '3px' }} />
                </div>
                <span style={{ fontSize: '11px', color: THEME.colors.textMuted, width: '70px', textAlign: 'right' }}>{wr}% ({d.total})</span>
              </div>
            );
          })}
          <div style={{ marginTop: '10px', fontSize: '11px', color: THEME.colors.textMuted, fontStyle: 'italic' }}>
            10+ trades नसतील तर % वर विश्वास ठेवू नका — फक्त दिशा बघा.
          </div>
        </div>
      )}

      {open.length > 0 && (
        <div style={S.card()}>
          <div style={S.section}>अजून चालू असलेले (target/SL ची वाट बघतायत)</div>
          {open.slice(-5).reverse().map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
              <span style={{ color: t.direction === 'BUY' ? THEME.colors.green : THEME.colors.red, fontWeight: '700' }}>{t.direction} {extractTF(t)}</span>
              <span style={{ color: THEME.colors.textMuted }}>Entry ${t.entry} → T1 ${t.tp1}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────
export default function StatsScreen() {
  const [tab, setTab] = useState('overview');

  const trades      = getTrades();
  const stats       = useMemo(() => buildStats(trades),        [trades]);
  const breakdowns  = useMemo(() => buildBreakdowns(trades),   [trades]);
  const curve       = useMemo(() => buildEquityCurve(trades),  [trades]);
  const psychStats  = useMemo(() => buildPsychStats(trades),   [trades]);

  const tabs = [
    { id: 'jarvis', label: 'Jarvis Accuracy' },
    { id: 'overview',   label: '📊 Overview'   },
    { id: 'breakdown',  label: '🔍 Breakdown'  },
    { id: 'equity',     label: '📈 Equity'     },
    { id: 'psych',      label: '🧘 Psychology' },
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>📈 STATS & PERFORMANCE</div>
      <div style={S.sub}>WIN RATE · P&L · BREAKDOWNS · EQUITY CURVE · PSYCHOLOGY</div>

      {/* Summary strip */}
      {stats && (
        <div style={{ ...S.card('rgba(0,212,255,0.06)'), padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px' }}>
            <span style={{ color: THEME.colors.gold, fontWeight: '700' }}>{stats.total}</span>
            <span style={{ color: THEME.colors.textMuted }}> closed · </span>
            <span style={{ color: THEME.colors.green, fontWeight: '700' }}>{stats.winRate}%</span>
            <span style={{ color: THEME.colors.textMuted }}> win rate</span>
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: pnlColor(stats.totalPnl) }}>
            {stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.btn(tab === t.id), flex: 1, padding: '7px 4px' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'jarvis'    && <JarvisAccuracyTab trades={trades}                          />}
      {tab === 'overview'  && <OverviewTab  stats={stats}            trades={trades}     />}
      {tab === 'breakdown' && <BreakdownTab breakdowns={breakdowns}                      />}
      {tab === 'equity'    && <EquityTab    curve={curve}                                />}
      {tab === 'psych'     && <PsychTab     psychStats={psychStats}                      />}
    </div>
  );
}


